import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { 
  Zap, 
  Clock, 
  Shield, 
  Check, 
  FileText,
  CheckCircle,
  Sparkles,
  MousePointer,
  Users, 
  Mail, 
  User,
  FileCheck,
  Calendar,
  Pen,
  Type,
  Upload,
  Globe,
  Eye,
  Activity,
  Database,
  Download,
  Lock,
  Phone,
  MapPin,
  DollarSign
} from 'lucide-react';

// Import Yellowtail font for signature
if (typeof document !== 'undefined' && !document.head.querySelector('link[data-yellowtail-font]')) {
  const link = document.createElement('link');
  link.setAttribute('data-yellowtail-font', '');
  link.href = 'https://fonts.googleapis.com/css2?family=Yellowtail&display=swap';
  link.rel = 'stylesheet';
  document.head.appendChild(link);
}

// Shared styles - matching existing design system
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#4b5563',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
  border: '#e6e6e6'
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

// One-Click Signature Demo
export const OneClickSignatureDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showSignature, setShowSignature] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [timer, setTimer] = useState(0);
  const [signatureProgress, setSignatureProgress] = useState(0);
  const [showSparkles, setShowSparkles] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showCursor, setShowCursor] = useState(false);
  const demoRef = useRef(null);
  const signatureFieldRef = useRef(null);

  // Demo steps - much faster and more dramatic
  const steps = useMemo(() => [
    { id: 'cursor-move', duration: 800, description: 'Cursor moves to signature field' },
    { id: 'hover', duration: 300, description: 'Field highlights on hover' },
    { id: 'click', duration: 50, description: 'Single click to sign' },
    { id: 'signature-draw', duration: 1200, description: 'Handwritten signature appears' },
    { id: 'validation', duration: 150, description: 'Instant legal validation' },
    { id: 'complete', duration: 300, description: 'Document secured' }
  ], []);



  // Auto-play demo when component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isPlaying) {
          setTimeout(() => {
            setIsPlaying(true);
          }, 800);
        }
      },
      { threshold: 0.4 }
    );

    const currentRef = demoRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isPlaying]);

  // Timer effect
  useEffect(() => {
    if (isPlaying && !showCompletion) {
      const interval = setInterval(() => {
        setTimer(prev => prev + 0.1);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isPlaying, showCompletion]);

  // Step progression with enhanced animations
  useEffect(() => {
    if (!isPlaying) return;

    if (currentStep < steps.length) {
      const step = steps[currentStep];
      const stepTimer = setTimeout(() => {
        
        if (step.id === 'cursor-move') {
          setShowCursor(true);
          // Move cursor to signature field
          if (signatureFieldRef.current) {
            const rect = signatureFieldRef.current.getBoundingClientRect();
            const containerRect = demoRef.current.getBoundingClientRect();
            setCursorPosition({
              x: rect.left - containerRect.left + rect.width / 2,
              y: rect.top - containerRect.top + rect.height / 2
            });
          }
        } else if (step.id === 'click') {
          setShowSparkles(true);
          setTimeout(() => setShowSparkles(false), 500);
        } else if (step.id === 'signature-draw') {
          setShowSignature(true);
          // Animate signature drawing
          const drawInterval = setInterval(() => {
            setSignatureProgress(prev => {
              if (prev >= 100) {
                clearInterval(drawInterval);
                return 100;
              }
              return prev + 8; // Smooth drawing animation
            });
          }, 50);
        } else if (step.id === 'complete') {
          setShowCompletion(true);
          setShowCursor(false);
        }
        
        setCurrentStep(prev => prev + 1);
      }, step.duration);

      return () => clearTimeout(stepTimer);
    }
  }, [isPlaying, currentStep, steps]);

  // Reset demo
  const resetDemo = () => {
    setIsPlaying(false);
    setCurrentStep(0);
    setShowSignature(false);
    setShowCompletion(false);
    setTimer(0);
    setSignatureProgress(0);
    setShowSparkles(false);
    setShowCursor(false);
    setCursorPosition({ x: 0, y: 0 });
    
    setTimeout(() => {
      setIsPlaying(true);
    }, 2000);
  };

  // Auto-reset after completion
  useEffect(() => {
    if (showCompletion) {
      const resetTimer = setTimeout(() => {
        resetDemo();
      }, 6000);
      return () => clearTimeout(resetTimer);
    }
  }, [showCompletion]);

  // SVG path for handwritten signature animation
  const signaturePath = "M10,50 Q30,20 50,40 T90,35 Q110,25 130,45 T170,40 Q190,30 210,50 T250,45";

  return (
    <div ref={demoRef} className="p-6 h-full" style={{ background: colors.white, fontFamily: fontStyles.fontFamily }}>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs" style={{ color: colors.gray500, ...fontStyles.body }}>
            One-Click Signature Demo
          </div>
          <div className="flex items-center mt-1">
            <div className="text-lg font-bold mr-3" style={{ color: colors.accent, ...fontStyles.heading }}>
              {timer.toFixed(1)}s
            </div>
            <div className="px-2 py-1 rounded-full text-xs" style={{ 
              backgroundColor: showCompletion ? colors.successLight : colors.accentLight,
              color: showCompletion ? colors.success : colors.accent,
              ...fontStyles.medium
            }}>
              {showCompletion ? 'Signed!' : isPlaying ? 'Signing...' : 'Ready'}
            </div>
          </div>
        </div>
        <button
          onClick={resetDemo}
          className="flex items-center px-3 py-2 rounded-lg border hover:bg-gray-50 transition-all text-xs"
          style={{ 
            borderColor: colors.border, 
            color: colors.gray700,
            ...fontStyles.medium
          }}
        >
          <Zap size={12} className="mr-1" />
          Replay
        </button>
      </div>

      <div className="w-full max-w-4xl mx-auto">
        {/* Main Document Demo */}
        <div>
          <div className="bg-white border rounded-xl p-8 h-full relative overflow-hidden" style={{ borderColor: colors.border, boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)' }}>
            
            {/* Animated cursor */}
            <AnimatePresence>
              {showCursor && (
                <motion.div
                  className="absolute z-50 pointer-events-none"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1,
                    x: cursorPosition.x - 12,
                    y: cursorPosition.y - 12
                  }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{ type: "spring", stiffness: 200, damping: 20 }}
                >
                  <MousePointer size={24} style={{ color: colors.accent }} />
                  
                  {/* Click effect */}
                  {showSparkles && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.5, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <Sparkles size={32} style={{ color: colors.warning }} />
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Document Header */}
            <div className="flex items-center mb-8 pb-6 border-b" style={{ borderColor: colors.border }}>
              <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl mr-4 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                SP
              </div>
              <div>
                <div className="font-bold text-2xl mb-1" style={{ color: colors.gray900, ...fontStyles.heading }}>
                  Consulting Agreement
                </div>
                <div className="text-sm flex items-center" style={{ color: colors.gray500, ...fontStyles.body }}>
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></div>
                  Ready for signature
                </div>
              </div>
            </div>

            {/* Document Content - Simplified */}
            <div className="space-y-6 mb-10">
              <div className="text-base leading-relaxed" style={{ color: colors.gray700, ...fontStyles.body }}>
                This Consulting Agreement establishes the terms between <strong>SignPact Inc.</strong> and <strong>TechCorp Inc.</strong> for professional AI consulting services.
              </div>
              
              <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 p-6 rounded-xl border" style={{ borderColor: colors.border }}>
                <div className="grid grid-cols-2 gap-6 text-sm">
                  <div className="space-y-3">
                    <div><strong>Client:</strong> TechCorp Inc.</div>
                    <div><strong>Consultant:</strong> Ranvir Deshmukh</div>
                  </div>
                  <div className="space-y-3">
                    <div><strong>Start Date:</strong> January 15, 2024</div>
                    <div><strong>Duration:</strong> 6 months</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Signature Field - The Star of the Show */}
            <div className="relative">
              <div className="text-lg font-semibold mb-4" style={{ color: colors.gray900, ...fontStyles.heading }}>
                Client Signature:
              </div>
              
              <motion.div
                ref={signatureFieldRef}
                className="relative border-3 border-dashed rounded-2xl p-8 h-32 cursor-pointer transition-all duration-500 overflow-hidden"
                style={{
                  borderColor: showSignature ? colors.success : (currentStep >= 1 ? colors.accent : colors.gray300),
                  backgroundColor: showSignature ? colors.successLight : (currentStep >= 1 ? colors.accentLight : colors.gray50),
                  borderWidth: '3px'
                }}
                whileHover={{ 
                  scale: !isPlaying ? 1.02 : 1,
                  boxShadow: !isPlaying ? '0 10px 30px rgba(30, 64, 175, 0.15)' : undefined
                }}
                animate={{
                  borderColor: showSignature ? colors.success : (currentStep >= 1 ? colors.accent : colors.gray300),
                  backgroundColor: showSignature ? colors.successLight : (currentStep >= 1 ? colors.accentLight : colors.gray50),
                  scale: currentStep === 2 ? [1, 1.02, 1] : 1
                }}
                transition={{ duration: 0.3 }}
              >
                {!showSignature ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <motion.div
                        animate={{ 
                          scale: currentStep >= 1 ? [1, 1.1, 1] : 1,
                          rotate: currentStep >= 1 ? [0, 5, -5, 0] : 0
                        }}
                        transition={{ duration: 0.5, repeat: currentStep >= 1 ? Infinity : 0, repeatDelay: 2 }}
                      >
                        <Zap size={32} className="mx-auto mb-3" style={{ color: colors.accent }} />
                      </motion.div>
                      <div className="text-lg font-semibold" style={{ color: colors.accent, ...fontStyles.heading }}>
                        Click anywhere to sign
                      </div>
                      <div className="text-sm mt-1" style={{ color: colors.gray600, ...fontStyles.body }}>
                        One click. Instant signature. Legally binding.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative h-full flex items-center">
                    {/* Handwritten signature SVG animation */}
                    <motion.svg
                      width="280"
                      height="80"
                      viewBox="0 0 280 80"
                      className="mx-auto"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.path
                        d={signaturePath}
                        stroke={colors.accent}
                        strokeWidth="3"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: signatureProgress / 100 }}
                        transition={{ duration: 1.2, ease: "easeInOut" }}
                        style={{
                          filter: 'drop-shadow(0 2px 4px rgba(30, 64, 175, 0.2))'
                        }}
                      />
                    </motion.svg>
                    
                    {/* Signature completion effect */}
                    {signatureProgress >= 100 && (
                      <motion.div
                        className="absolute top-2 right-4"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                      >
                        <CheckCircle size={28} style={{ color: colors.success }} />
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Magical click ripple effect */}
                {currentStep === 2 && (
                  <motion.div
                    className="absolute inset-0 border-3 rounded-2xl"
                    style={{ borderColor: colors.accent }}
                    initial={{ scale: 1, opacity: 1 }}
                    animate={{ scale: [1, 1.1, 1.2], opacity: [1, 0.5, 0] }}
                    transition={{ duration: 0.6 }}
                  />
                )}
              </motion.div>

              {/* Legal validation badges - Enhanced */}
              {signatureProgress >= 100 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6 flex items-center justify-center gap-4 text-sm"
                >
                  <motion.div 
                    className="flex items-center px-4 py-2 rounded-full shadow-sm"
                    style={{ backgroundColor: colors.successLight, color: colors.success }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6, type: "spring" }}
                  >
                    <Shield size={16} className="mr-2" />
                    UETA Compliant
                  </motion.div>
                  <motion.div 
                    className="flex items-center px-4 py-2 rounded-full shadow-sm"
                    style={{ backgroundColor: colors.successLight, color: colors.success }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8, type: "spring" }}
                  >
                    <Check size={16} className="mr-2" />
                    Legally Binding
                  </motion.div>
                  <motion.div 
                    className="flex items-center px-4 py-2 rounded-full shadow-sm"
                    style={{ backgroundColor: colors.successLight, color: colors.success }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.0, type: "spring" }}
                  >
                    <Clock size={16} className="mr-2" />
                    Instant Audit Trail
                  </motion.div>
                </motion.div>
              )}
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

// Autofill Signature Demo
export const AutofillSignatureDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoplayComplete, setAutoplayComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showHighlight, setShowHighlight] = useState(false);

  const [focusedField, setFocusedField] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showSignatureOptions, setShowSignatureOptions] = useState(false);
  const [selectedSignatureType, setSelectedSignatureType] = useState('typed');
  const [showFinalSignature, setShowFinalSignature] = useState(false);
  const demoRef = useRef(null);
  
  // Mouse position values for subtle hover effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springMouseX = useSpring(mouseX, { stiffness: 100, damping: 15 });
  const springMouseY = useSpring(mouseY, { stiffness: 100, damping: 15 });
  
  // Simulated user data from the database
  const userData = {
    company: "TechCorp, Inc.",
    name: "Ranvir Deshmukh",
    title: "AI Engineer",
    email: "ranvir@techcorp.com",
    phone: "(415) 555-1234",
    address: "123 Innovation Ave, San Francisco, CA 94105",
    startDate: "January 15, 2024",
    rate: "$150.00 per hour",
    term: "6 months",
    completed: "42 contracts"
  };
  
  // Fields to autofill - focused on signature-relevant fields
  const contractFields = [
    { id: "name", label: "Signatory Name", value: userData.name, icon: <User size={16} /> },
    { id: "title", label: "Title", value: userData.title, icon: <User size={16} /> },
    { id: "company", label: "Company", value: userData.company, icon: <FileCheck size={16} /> },
    { id: "email", label: "Email", value: userData.email, icon: <Mail size={16} /> },
    { id: "startDate", label: "Date", value: userData.startDate, icon: <Calendar size={16} /> },
  ];

  // Animation steps - faster for signature demo
  const steps = contractFields.map((field, index) => ({
    id: field.id,
    delay: index === 0 ? 0.8 : 0.6,
    typingDuration: 0.6,
    pauseBefore: 0.2,
  }));
  
  // Handle mouse movement for hover effect
  const handleMouseMove = (e) => {
    if (!demoRef.current) return;
    const rect = demoRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    mouseX.set(x);
    mouseY.set(y);
  };
  
  // Reset demo when complete
  useEffect(() => {
    if (autoplayComplete) {
      const timer = setTimeout(() => {
        setIsPlaying(false);
        setAutoplayComplete(false);
        setCurrentStep(0);
        setShowHighlight(false);
        setFocusedField(null);
        setProgress(0);
        setShowSignatureOptions(false);
        setShowFinalSignature(false);
      }, 6000);
      
      return () => clearTimeout(timer);
    }
  }, [autoplayComplete]);
  
  // Step through the animation sequence
  useEffect(() => {
    if (!isPlaying) return;
    
    if (currentStep < steps.length) {
      const progressPercentage = (currentStep / steps.length) * 100;
      setProgress(progressPercentage);
      
      const step = steps[currentStep];
      
      const cursorTimer = setTimeout(() => {
        // Get field position dynamically
        const fieldElement = demoRef.current?.querySelector(`[data-field-id="${step.id}"]`);
        if (fieldElement && demoRef.current) {
          const fieldRect = fieldElement.getBoundingClientRect();
          const containerRect = demoRef.current.getBoundingClientRect();
          mouseX.set(fieldRect.left - containerRect.left + fieldRect.width / 2);
          mouseY.set(fieldRect.top - containerRect.top + fieldRect.height / 2);
        }
        
        setTimeout(() => {
          setShowHighlight(true);
          
          const typingTimer = setTimeout(() => {
            setFocusedField(step.id);
            
            const nextStepTimer = setTimeout(() => {
              setCurrentStep(prevStep => prevStep + 1);
              setShowHighlight(false);
            }, (step.typingDuration + step.pauseBefore) * 1000);
            
            return () => clearTimeout(nextStepTimer);
          }, 200);
          
          return () => clearTimeout(typingTimer);
        }, 100);
        
      }, step.delay * 1000);
      
      return () => clearTimeout(cursorTimer);
    } else {
      setProgress(100);
      
      // Show signature options after autofill
      const signatureTimer = setTimeout(() => {
        setShowSignatureOptions(true);
        
        // Auto-select signature type and show final signature
        setTimeout(() => {
          setShowFinalSignature(true);
          
          setTimeout(() => {
            setAutoplayComplete(true);
          }, 1500);
        }, 2000);
      }, 800);
      
      return () => clearTimeout(signatureTimer);
    }
  }, [isPlaying, currentStep, steps, mouseX, mouseY, demoRef]);
  
  // Use Intersection Observer to detect when component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isPlaying && !autoplayComplete) {
          setTimeout(() => {
            setIsPlaying(true);
          }, 800);
        }
      },
      { threshold: 0.4 }
    );

    const currentRef = demoRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isPlaying, autoplayComplete]);

  return (
    <div className="p-6 h-full" style={{ background: colors.white, fontFamily: fontStyles.fontFamily }}>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs" style={{ color: colors.gray500, ...fontStyles.body }}>
            Smart Auto-Fill + Signature Demo
          </div>
          <div className="flex items-center mt-1">
            <div className="px-2 py-1 rounded-full text-xs" style={{ 
              backgroundColor: autoplayComplete ? colors.successLight : colors.accentLight,
              color: autoplayComplete ? colors.success : colors.accent,
              ...fontStyles.medium
            }}>
              {autoplayComplete ? 'Complete' : isPlaying ? 'Auto-filling...' : 'Ready'}
            </div>
            {isPlaying && !autoplayComplete && (
              <div className="ml-3 flex items-center">
                <div className="h-1.5 bg-blue-500 rounded-full" style={{ width: `${progress}%`, minWidth: '40px', maxWidth: '80px' }} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-7 gap-6 h-full">
        {/* Main Document */}
        <div className="lg:col-span-5">
          <div 
            className="bg-white border rounded-lg p-6 h-full relative overflow-hidden"
            style={{ borderColor: colors.border }}
            onMouseMove={handleMouseMove}
          >
            {/* Document Header */}
            <div className="flex justify-between items-center border-b pb-4 mb-6" style={{ borderColor: colors.border }}>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-600 rounded mr-3 flex items-center justify-center text-white font-bold">SP</div>
                <div>
                  <div className="font-bold text-xl text-gray-900">Service Agreement</div>
                  <div className="text-sm text-gray-500">Ready for signature</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-md text-sm">Draft</div>
              </div>
            </div>
            
            {/* Contract Content */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold mb-4">SIGNATORY INFORMATION</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Full Name</label>
                    <motion.div 
                      className={`border rounded-lg p-3 ${focusedField === "name" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                      data-field-id="name"
                    >
                      <span className={`${focusedField === "name" || currentStep > steps.findIndex(s => s.id === "name") ? "text-blue-700 font-medium" : "text-gray-400"}`}>
                        {focusedField === "name" || currentStep > steps.findIndex(s => s.id === "name") ? userData.name : "Enter full name..."}
                        {focusedField === "name" && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </span>
                    </motion.div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                    <motion.div 
                      className={`border rounded-lg p-3 ${focusedField === "title" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                      data-field-id="title"
                    >
                      <span className={`${focusedField === "title" || currentStep > steps.findIndex(s => s.id === "title") ? "text-blue-700 font-medium" : "text-gray-400"}`}>
                        {focusedField === "title" || currentStep > steps.findIndex(s => s.id === "title") ? userData.title : "Enter title..."}
                        {focusedField === "title" && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </span>
                    </motion.div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Company</label>
                    <motion.div 
                      className={`border rounded-lg p-3 ${focusedField === "company" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                      data-field-id="company"
                    >
                      <span className={`${focusedField === "company" || currentStep > steps.findIndex(s => s.id === "company") ? "text-blue-700 font-medium" : "text-gray-400"}`}>
                        {focusedField === "company" || currentStep > steps.findIndex(s => s.id === "company") ? userData.company : "Enter company name..."}
                        {focusedField === "company" && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </span>
                    </motion.div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                    <motion.div 
                      className={`border rounded-lg p-3 ${focusedField === "email" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                      data-field-id="email"
                    >
                      <span className={`${focusedField === "email" || currentStep > steps.findIndex(s => s.id === "email") ? "text-blue-700 font-medium" : "text-gray-400"}`}>
                        {focusedField === "email" || currentStep > steps.findIndex(s => s.id === "email") ? userData.email : "Enter email..."}
                        {focusedField === "email" && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </span>
                    </motion.div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Date</label>
                    <motion.div 
                      className={`border rounded-lg p-3 ${focusedField === "startDate" ? "bg-blue-50 border-blue-300" : "border-gray-200"}`}
                      data-field-id="startDate"
                    >
                      <span className={`${focusedField === "startDate" || currentStep > steps.findIndex(s => s.id === "startDate") ? "text-blue-700 font-medium" : "text-gray-400"}`}>
                        {focusedField === "startDate" || currentStep > steps.findIndex(s => s.id === "startDate") ? userData.startDate : "Select date..."}
                        {focusedField === "startDate" && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </span>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Signature Section */}
              <div className="border-t pt-6" style={{ borderColor: colors.border }}>
                <h3 className="text-lg font-bold mb-4">DIGITAL SIGNATURE</h3>
                
                {/* Signature Options */}
                {showSignatureOptions && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4"
                  >
                    <div className="text-sm font-medium text-gray-700 mb-3">Choose signature style:</div>
                    <div className="flex gap-3">
                      {[
                        { id: 'typed', icon: Type, label: 'Typed' },
                        { id: 'drawn', icon: Pen, label: 'Draw' },
                        { id: 'upload', icon: Upload, label: 'Upload' }
                      ].map((option) => (
                        <motion.button
                          key={option.id}
                          className={`flex items-center px-4 py-2 rounded-lg border transition-all ${
                            selectedSignatureType === option.id 
                              ? 'border-blue-500 bg-blue-50 text-blue-700' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedSignatureType(option.id)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <option.icon size={16} className="mr-2" />
                          {option.label}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Signature Field */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {!showFinalSignature ? (
                    <div>
                      <Pen size={32} className="mx-auto mb-3 text-gray-400" />
                      <div className="text-gray-500">
                        {showSignatureOptions ? 'Creating your signature...' : 'Signature will appear here'}
                      </div>
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                      className="flex items-center justify-center"
                    >
                      <div className="text-3xl font-script text-blue-600 mr-4">
                        Ranvir Deshmukh
                      </div>
                      <div className="text-sm text-gray-500">
                        Signed on {userData.startDate}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Legal Validation */}
                {showFinalSignature && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-4 flex items-center gap-4 text-sm"
                  >
                    <div className="flex items-center text-green-600">
                      <Shield size={16} className="mr-1" />
                      Legally Binding
                    </div>
                    <div className="flex items-center text-green-600">
                      <Check size={16} className="mr-1" />
                      UETA Compliant
                    </div>
                    <div className="flex items-center text-green-600">
                      <Clock size={16} className="mr-1" />
                      Audit Trail Created
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Animated cursor */}
            <AnimatePresence>
              {isPlaying && !autoplayComplete && !showSignatureOptions && (
                <motion.div 
                  className="absolute z-40 pointer-events-none"
                  style={{ 
                    x: springMouseX,
                    y: springMouseY,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.95 }}
                  exit={{ opacity: 0 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M4 3L19 12L4 21V3Z" fill="rgba(37, 99, 235, 0.85)" />
                  </svg>
                  
                  {/* Highlight circle */}
                  <motion.div 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
                    style={{ borderColor: 'rgba(59, 130, 246, 0.8)' }}
                    initial={{ width: 0, height: 0, opacity: 0 }}
                    animate={{ 
                      width: showHighlight ? 60 : 0, 
                      height: showHighlight ? 60 : 0,
                      opacity: showHighlight ? [0, 0.7, 0] : 0
                    }}
                    transition={{ duration: 1.2 }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Data Sources Panel */}
        <div className="lg:col-span-2 bg-gradient-to-br from-gray-50 to-blue-50/30 border rounded-lg p-4" style={{ borderColor: colors.border }}>
          <div className="flex items-center mb-4">
            <Zap size={16} className="text-blue-600 mr-2" />
            <div className="text-sm font-medium text-gray-800">Smart Data Sources</div>
          </div>
          
          <div className="space-y-3">
            <div className="p-3 bg-white rounded-lg border" style={{ borderColor: colors.border }}>
              <div className="flex items-center mb-2">
                <Users size={14} className="text-blue-600 mr-2" />
                <div className="text-sm font-medium">User Profile</div>
              </div>
              <div className="text-xs text-gray-500">Name, title, contact info</div>
            </div>
            
            <div className="p-3 bg-white rounded-lg border" style={{ borderColor: colors.border }}>
              <div className="flex items-center mb-2">
                <FileText size={14} className="text-blue-600 mr-2" />
                <div className="text-sm font-medium">Previous Contracts</div>
              </div>
              <div className="text-xs text-gray-500">{userData.completed} in history</div>
            </div>
            
            <div className="p-3 bg-white rounded-lg border" style={{ borderColor: colors.border }}>
              <div className="flex items-center mb-2">
                <Zap size={14} className="text-blue-600 mr-2" />
                <div className="text-sm font-medium">AI Prediction</div>
              </div>
              <div className="text-xs text-gray-500">Smart field matching</div>
            </div>
          </div>

          {/* Time Saved */}
          {currentStep > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-3 bg-white rounded-lg border"
              style={{ borderColor: colors.border }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-700">Time Saved</div>
                <div className="text-sm font-bold text-green-600">
                  {Math.min(currentStep * 8, 40)}s
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-green-500 rounded-full"
                  style={{ width: `${(currentStep / steps.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </motion.div>
          )}

          {/* Security Badge */}
          <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center mb-2">
              <Shield size={14} className="text-green-600 mr-2" />
              <div className="text-sm font-medium text-green-800">Enterprise Security</div>
            </div>
            <div className="text-xs text-green-600">256-bit encryption • Global standards compliant</div>
          </div>
        </div>
      </div>

      {/* Completion overlay */}
      <AnimatePresence>
        {autoplayComplete && (
          <motion.div 
            className="absolute inset-0 bg-blue-600 bg-opacity-90 flex flex-col items-center justify-center rounded-lg z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="text-center text-white"
            >
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Document Auto-Filled & Signed!</h3>
              <p className="text-blue-100 mb-4">All fields populated and signature applied in seconds</p>
              <div className="flex items-center justify-center gap-4 text-sm">
                <span>⚡ 3x faster than manual</span>
                <span>🔒 Legally binding</span>
                <span>✅ Zero errors</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Compliance Demo
export const ComplianceDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [validatedCompliances, setValidatedCompliances] = useState([]);
  const [showGlobalCoverage, setShowGlobalCoverage] = useState(false);

  // Compliance standards
  const complianceStandards = useMemo(() => [
    { 
      id: 'ueta', 
      name: 'UETA', 
      fullName: 'Uniform Electronic Transactions Act',
      region: 'United States',
      coverage: '47 US States',
      icon: Shield,
      color: colors.success,
      description: 'Legal framework for electronic signatures in the US'
    },
    { 
      id: 'esign', 
      name: 'ESIGN Act', 
      fullName: 'Electronic Signatures in Global and National Commerce Act',
      region: 'United States',
      coverage: 'Federal Law',
      icon: FileText,
      color: colors.accent,
      description: 'Federal legislation ensuring e-signature validity'
    },
    { 
      id: 'eidas', 
      name: 'eIDAS', 
      fullName: 'Electronic Identification and Trust Services',
      region: 'European Union',
      coverage: '27 EU Countries',
      icon: Globe,
      color: colors.warning,
      description: 'EU regulation for electronic identification and signatures'
    },
    { 
      id: 'pipeda', 
      name: 'PIPEDA', 
      fullName: 'Personal Information Protection and Electronic Documents Act',
      region: 'Canada',
      coverage: 'Federal Canada',
      icon: Lock,
      color: colors.error,
      description: 'Canadian privacy and electronic signature legislation'
    },
    { 
      id: 'gdpr', 
      name: 'GDPR', 
      fullName: 'General Data Protection Regulation',
      region: 'European Union',
      coverage: 'Global Reach',
      icon: Users,
      color: colors.accent,
      description: 'Comprehensive data protection and privacy regulation'
    }
  ], []);

  // Global compliance standards
  const globalStandards = [
    { name: 'Global Standards', icon: Globe, verified: true, description: 'Compliant with international e-signature laws' },
    { name: 'Legal Framework', icon: Shield, verified: true, description: 'UETA, eIDAS, and ESIGN Act compliant' },
    { name: 'Data Protection', icon: Lock, verified: true, description: 'Privacy regulations compliant worldwide' }
  ];



  // Auto-play demo
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isPlaying) {
          setTimeout(() => {
            setIsPlaying(true);
          }, 500);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('compliance-demo');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [isPlaying]);

  // Step progression
  useEffect(() => {
    if (!isPlaying) return;

    if (currentStep < complianceStandards.length) {
      const timer = setTimeout(() => {
        setValidatedCompliances(prev => [...prev, complianceStandards[currentStep].id]);
        setCurrentStep(prev => prev + 1);
      }, 800);

      return () => clearTimeout(timer);
    } else {
      // Show global coverage
      const timer1 = setTimeout(() => setShowGlobalCoverage(true), 1000);
      
      return () => {
        clearTimeout(timer1);
      };
    }
  }, [isPlaying, currentStep, complianceStandards]);

  // Auto-reset
  useEffect(() => {
    if (showGlobalCoverage) {
      const timer = setTimeout(() => {
        setIsPlaying(false);
        setCurrentStep(0);
        setValidatedCompliances([]);
        setShowGlobalCoverage(false);
        setTimeout(() => setIsPlaying(true), 1000);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showGlobalCoverage]);

  return (
    <div id="compliance-demo" className="p-6 h-full" style={{ background: colors.white, fontFamily: fontStyles.fontFamily }}>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs" style={{ color: colors.gray500, ...fontStyles.body }}>
            Legal Compliance Validation
          </div>
          <div className="flex items-center mt-1">
            <div className="px-2 py-1 rounded-full text-xs" style={{ 
              backgroundColor: showGlobalCoverage ? colors.successLight : colors.accentLight,
              color: showGlobalCoverage ? colors.success : colors.accent,
              ...fontStyles.medium
            }}>
              {showGlobalCoverage ? 'Validated' : isPlaying ? 'Checking...' : 'Ready'}
            </div>
            <div className="ml-3 text-sm" style={{ color: colors.gray600, ...fontStyles.body }}>
              {validatedCompliances.length}/{complianceStandards.length} standards verified
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
        {/* Compliance Standards */}
        <div className="lg:col-span-2 space-y-4">
          <div className="text-lg font-bold mb-4" style={{ color: colors.gray900, ...fontStyles.heading }}>
            Global Compliance Standards
          </div>
          
          {complianceStandards.map((standard, index) => {
            const isValidated = validatedCompliances.includes(standard.id);
            const isValidating = currentStep === index && isPlaying;
            const Icon = standard.icon;

            return (
              <motion.div
                key={standard.id}
                className="border rounded-xl p-4 transition-all duration-300"
                style={{
                  borderColor: isValidated ? colors.accent : colors.border,
                  backgroundColor: isValidated ? colors.accentLight : colors.white,
                  boxShadow: isValidating ? '0 4px 12px rgba(30, 64, 175, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
                animate={{
                  scale: isValidating ? [1, 1.02, 1] : 1,
                  borderColor: isValidated ? colors.accent : colors.border
                }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                      style={{
                        backgroundColor: isValidated ? colors.accent : standard.color + '20',
                        color: isValidated ? colors.white : standard.color
                      }}
                    >
                      {isValidated ? (
                        <Check size={24} />
                      ) : (
                        <Icon size={24} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="text-lg font-bold" style={{ color: colors.gray900, ...fontStyles.heading }}>
                          {standard.name}
                        </div>
                        <div className="text-xs px-2 py-1 rounded-full" style={{ 
                          backgroundColor: standard.color + '20', 
                          color: standard.color,
                          ...fontStyles.medium
                        }}>
                          {standard.region}
                        </div>
                      </div>
                      <div className="text-sm" style={{ color: colors.gray600, ...fontStyles.body }}>
                        {standard.fullName}
                      </div>
                      <div className="text-xs mt-1" style={{ color: colors.gray500, ...fontStyles.body }}>
                        Coverage: {standard.coverage} • {standard.description}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    {isValidating && (
                      <motion.div
                        className="w-6 h-6 border-2 border-t-transparent rounded-full mr-3"
                        style={{ borderColor: colors.accent }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    )}
                    {isValidated && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: colors.success, color: colors.white, ...fontStyles.medium }}
                      >
                        ✓ Verified
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Global Coverage Map */}
          {showGlobalCoverage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border rounded-xl p-6"
              style={{ borderColor: colors.border, backgroundColor: colors.gray50 }}
            >
              <div className="flex items-center mb-4">
                <Globe size={20} className="mr-2" style={{ color: colors.accent }} />
                <div className="text-lg font-bold" style={{ color: colors.gray900, ...fontStyles.heading }}>
                  Global Legal Coverage
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-white rounded-lg border" style={{ borderColor: colors.border }}>
                  <div className="text-2xl font-bold" style={{ color: colors.success, ...fontStyles.heading }}>
                    180+
                  </div>
                  <div className="text-sm" style={{ color: colors.gray600, ...fontStyles.body }}>
                    Countries
                  </div>
                </div>
                <div className="p-3 bg-white rounded-lg border" style={{ borderColor: colors.border }}>
                  <div className="text-2xl font-bold" style={{ color: colors.accent, ...fontStyles.heading }}>
                    99.7%
                  </div>
                  <div className="text-sm" style={{ color: colors.gray600, ...fontStyles.body }}>
                    Global GDP
                  </div>
                </div>
                <div className="p-3 bg-white rounded-lg border" style={{ borderColor: colors.border }}>
                  <div className="text-2xl font-bold" style={{ color: colors.warning, ...fontStyles.heading }}>
                    24/7
                  </div>
                  <div className="text-sm" style={{ color: colors.gray600, ...fontStyles.body }}>
                    Monitoring
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Global Compliance & Comparisons */}
        <div className="lg:col-span-1 space-y-6">
          {/* Global Compliance Standards */}
          <div className="border rounded-xl p-4" style={{ borderColor: colors.border, backgroundColor: colors.white }}>
            <div className="flex items-center mb-4">
              <Globe size={18} className="mr-2" style={{ color: colors.accent }} />
              <div className="text-sm font-bold" style={{ color: colors.gray900, ...fontStyles.heading }}>
                Global Compliance
              </div>
            </div>
            
            <div className="space-y-3">
              {globalStandards.map((standard, index) => {
                const Icon = standard.icon;
                return (
                  <motion.div
                    key={standard.name}
                    className="flex items-center justify-between p-2 rounded-lg"
                    style={{ backgroundColor: colors.gray50 }}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 2 }}
                  >
                    <div className="flex items-center">
                      <Icon size={16} className="mr-2" style={{ color: colors.accent }} />
                      <div>
                        <div className="text-sm font-medium" style={{ ...fontStyles.medium }}>
                          {standard.name}
                        </div>
                        <div className="text-xs" style={{ color: colors.gray500, ...fontStyles.body }}>
                          {standard.description}
                        </div>
                      </div>
                    </div>
                    <CheckCircle size={16} style={{ color: colors.accent }} />
                  </motion.div>
                );
              })}
            </div>
          </div>



          {/* Live Compliance Score */}
          <div className="border rounded-xl p-4" style={{ borderColor: colors.accent, backgroundColor: colors.accentLight }}>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2" style={{ color: colors.accent, ...fontStyles.heading }}>
                100%
              </div>
              <div className="text-sm font-medium" style={{ color: colors.accent, ...fontStyles.medium }}>
                Compliance Score
              </div>
              <div className="text-xs mt-1" style={{ color: colors.gray600, ...fontStyles.body }}>
                All signatures legally binding worldwide
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Audit Trail Demo
export const AuditTrailDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(0);
  const [showRealTimeData, setShowRealTimeData] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [liveUpdates, setLiveUpdates] = useState(0);

  // Simple, clear audit trail events
  const auditEvents = [
    {
      id: 1,
      timestamp: '2:23 PM',
      event: 'Document Created',
      description: 'Service agreement uploaded and prepared',
      icon: FileText,
      details: {
        'File Type': 'PDF',
        'Size': '2.4 MB',
        'Pages': '3'
      }
    },
    {
      id: 2,
      timestamp: '2:24 PM',
      event: 'Document Opened',
      description: 'Recipient viewed the document',
      icon: Eye,
      details: {
        'View Time': '3m 42s',
        'Device': 'iPhone',
        'Location': 'New York'
      }
    },
    {
      id: 3,
      timestamp: '2:28 PM',
      event: 'Document Signed',
      description: 'Digital signature captured',
      icon: Pen,
      details: {
        'Method': 'Touch signature',
        'Signer': 'Sarah Johnson',
        'IP Address': '203.45.67.89'
      }
    },
    {
      id: 4,
      timestamp: '2:28 PM',
      event: 'Legally Validated',
      description: 'Signature verified and certified',
      icon: Shield,
      details: {
        'Standards': 'UETA, ESIGN Act',
        'Certificate': 'SP-2024-001547',
        'Status': 'Legally Binding'
      }
    },
    {
      id: 5,
      timestamp: '2:28 PM',
      event: 'Audit Trail Complete',
      description: 'Permanent record created',
      icon: CheckCircle,
      details: {
        'Security': 'SHA-256 Hash',
        'Storage': 'Encrypted Cloud',
        'Retention': '7 years'
      }
    }
  ];



  // Auto-play demo
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isPlaying) {
          setTimeout(() => {
            setIsPlaying(true);
          }, 500);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('audit-trail-demo');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [isPlaying]);

  // Enhanced step progression with live updates
  useEffect(() => {
    if (!isPlaying) return;

    if (currentEvent < auditEvents.length) {
      const timer = setTimeout(() => {
        setCurrentEvent(prev => prev + 1);
        setLiveUpdates(prev => prev + 1);
      }, 800); // Faster progression

      return () => clearTimeout(timer);
    } else {
      // Show real-time data
      const timer1 = setTimeout(() => setShowRealTimeData(true), 500);
      
      return () => {
        clearTimeout(timer1);
      };
    }
  }, [isPlaying, currentEvent, auditEvents.length]);

  // Live updates counter
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setLiveUpdates(prev => prev + 1);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isPlaying]);

  // Auto-reset
  useEffect(() => {
    if (showRealTimeData) {
      const timer = setTimeout(() => {
        setIsPlaying(false);
        setCurrentEvent(0);
        setShowRealTimeData(false);
        setActiveTab('timeline');
        setLiveUpdates(0);
        setTimeout(() => setIsPlaying(true), 1000);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showRealTimeData]);

  return (
    <div id="audit-trail-demo" className="p-6 h-full" style={{ background: colors.white, fontFamily: fontStyles.fontFamily }}>
      {/* Modern Header with Tabs */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs" style={{ color: colors.gray500, ...fontStyles.body }}>
              Instant Audit Trail Generation
            </div>
            <div className="flex items-center mt-1">
              <div className="px-2 py-1 rounded-full text-xs" style={{ 
                backgroundColor: showRealTimeData ? colors.successLight : colors.accentLight,
                color: showRealTimeData ? colors.success : colors.accent,
                ...fontStyles.medium
              }}>
                {showRealTimeData ? 'Live Recording' : isPlaying ? 'Generating...' : 'Ready'}
              </div>
              <div className="ml-3 text-sm" style={{ color: colors.gray600, ...fontStyles.body }}>
                {currentEvent}/{auditEvents.length} events recorded • Real-time
              </div>
            </div>
          </div>
          
          <motion.div 
            className="px-3 py-1 rounded-full text-xs"
            style={{ backgroundColor: colors.accent, color: colors.white }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Live Updates: {liveUpdates}
          </motion.div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg inline-flex">
          {['timeline', 'details', 'security'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === tab 
                  ? 'bg-white shadow-sm' 
                  : 'hover:bg-gray-200'
              }`}
              style={{ 
                color: activeTab === tab ? colors.accent : colors.gray600,
                ...fontStyles.medium
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full">
        {/* Enhanced Timeline */}
        <div className="lg:col-span-3">
          {activeTab === 'timeline' && (
            <div className="bg-white rounded-xl border p-6" style={{ borderColor: colors.border }}>
              {/* Simple Visual Timeline */}
              <div className="flex items-center justify-between mb-8">
                {auditEvents.map((event, index) => {
                  const isActive = index < currentEvent;
                  const isCurrent = index === currentEvent - 1 && isPlaying;
                  const Icon = event.icon;
                  
                  return (
                    <div key={event.id} className="flex flex-col items-center flex-1 relative">
                      {/* Connection Line */}
                      {index < auditEvents.length - 1 && (
                        <div 
                          className="absolute top-6 left-1/2 w-full h-0.5 z-0"
                          style={{
                            backgroundColor: isActive ? colors.accent : colors.gray200,
                            transform: 'translateX(50%)',
                            width: 'calc(100% - 24px)'
                          }}
                        />
                      )}
                      
                      {/* Step Circle */}
                      <motion.div
                        className="w-12 h-12 rounded-full flex items-center justify-center mb-3 relative z-10"
                        style={{
                          backgroundColor: isActive ? colors.accent : colors.gray200,
                          color: isActive ? colors.white : colors.gray500,
                          border: isCurrent ? `3px solid ${colors.accent}` : 'none',
                          boxShadow: isCurrent ? `0 0 0 6px ${colors.accent}20` : 'none'
                        }}
                        animate={{
                          scale: isCurrent ? [1, 1.1, 1] : 1
                        }}
                        transition={{ duration: 1, repeat: isCurrent ? Infinity : 0 }}
                      >
                        <Icon size={20} />
                      </motion.div>
                      
                      {/* Step Label */}
                      <div className="text-center">
                        <div 
                          className="text-xs font-medium mb-1"
                          style={{ 
                            color: isActive ? colors.accent : colors.gray500,
                            ...fontStyles.medium
                          }}
                        >
                          {event.event}
                        </div>
                        <div 
                          className="text-xs"
                          style={{ color: colors.gray500 }}
                        >
                          {event.timestamp}
                        </div>
                        {isActive && (
                          <motion.div
                            className="mt-1 px-2 py-0.5 rounded-full text-xs"
                            style={{ backgroundColor: colors.success, color: colors.white }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3 }}
                          >
                            ✓ Complete
                          </motion.div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Current Step Details */}
              <AnimatePresence mode="wait">
                {currentEvent > 0 && (
                  <motion.div
                    key={currentEvent}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-gray-50 rounded-lg p-4"
                  >
                    <div className="flex items-start">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
                        style={{ backgroundColor: colors.accent, color: colors.white }}
                      >
                        {React.createElement(auditEvents[currentEvent - 1]?.icon, { size: 16 })}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold mb-1" style={{ color: colors.gray900 }}>
                          {auditEvents[currentEvent - 1]?.event}
                        </div>
                        <div className="text-sm mb-3" style={{ color: colors.gray600 }}>
                          {auditEvents[currentEvent - 1]?.description}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          {auditEvents[currentEvent - 1] && Object.entries(auditEvents[currentEvent - 1].details).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span style={{ color: colors.gray500 }}>{key}:</span>
                              <span style={{ color: colors.gray900, fontWeight: 500 }}>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Details View */}
          {activeTab === 'details' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-gray-900 text-white p-6 rounded-xl font-mono text-sm"
              style={{ backgroundColor: '#1a1a1a' }}
            >
              <div className="flex items-center mb-4">
                <div className="w-2 h-2 rounded-full bg-green-400 mr-2"></div>
                <span className="text-green-400">Live Audit Log</span>
              </div>
              
              {auditEvents.slice(0, currentEvent).map((event, index) => (
                <motion.div
                  key={event.id}
                  className="mb-3 opacity-80"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="text-blue-400">[{event.timestamp}]</div>
                  <div className="text-yellow-300">{event.event}</div>
                  <div className="text-gray-300 ml-4">{event.description}</div>
                  <div className="text-gray-500 ml-4 text-xs">
                    {Object.entries(event.details).map(([key, value]) => (
                      <span key={key} className="mr-4">{key}: {value}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
              
              {isPlaying && (
                <motion.div
                  className="flex items-center text-green-400"
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <div className="w-2 h-2 rounded-full bg-green-400 mr-2"></div>
                  Recording in progress...
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Security View */}
          {activeTab === 'security' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border" style={{ borderColor: colors.accent + '30' }}>
                <div className="flex items-center mb-4">
                  <Shield size={20} className="mr-2" style={{ color: colors.accent }} />
                  <div className="font-semibold" style={{ color: colors.accent }}>Cryptographic Integrity</div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">Hash Algorithm:</span>
                    <span className="font-mono text-sm">SHA-256</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Signature:</span>
                    <span className="font-mono text-sm text-green-600">Verified</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Tamper Detection:</span>
                    <span className="font-mono text-sm text-green-600">Active</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <div className="flex items-center mb-4">
                  <Database size={20} className="mr-2 text-green-600" />
                  <div className="font-semibold text-green-700">Blockchain Record</div>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-gray-600">Block Hash:</div>
                    <div className="font-mono text-xs break-all">0x2f4a8c9e1d3b7a5f...</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Confirmations:</div>
                    <div className="font-semibold text-green-600">Instant</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Live Statistics Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {/* Live Event Counter */}
          <motion.div 
            className="border rounded-xl p-4 text-center"
            style={{ borderColor: colors.accent, backgroundColor: colors.accentLight }}
            animate={{ scale: currentEvent > 0 ? [1, 1.02, 1] : 1 }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="text-2xl font-bold mb-1" style={{ color: colors.accent }}>
              {currentEvent}
            </div>
            <div className="text-sm" style={{ color: colors.accent }}>Events Recorded</div>
            <div className="text-xs mt-1" style={{ color: colors.gray600 }}>
              Real-time tracking
            </div>
          </motion.div>

          {/* Security Status */}
          <div className="border rounded-xl p-4" style={{ borderColor: colors.border, backgroundColor: colors.white }}>
            <div className="flex items-center mb-3">
              <Shield size={16} className="mr-2" style={{ color: colors.accent }} />
              <div className="text-sm font-semibold" style={{ color: colors.gray900 }}>Security Status</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Encryption:</span>
                <span className="text-green-600 font-medium">AES-256</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Hash Integrity:</span>
                <span className="text-green-600 font-medium">Verified</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Tamper Seal:</span>
                <span className="text-green-600 font-medium">Active</span>
              </div>
            </div>
          </div>

          {/* Document Info */}
          <div className="border rounded-xl p-4" style={{ borderColor: colors.border, backgroundColor: colors.white }}>
            <div className="flex items-center mb-3">
              <FileText size={16} className="mr-2" style={{ color: colors.accent }} />
              <div className="text-sm font-semibold" style={{ color: colors.gray900 }}>Document Info</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Type:</span>
                <span style={{ color: colors.gray900 }}>Service Agreement</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Size:</span>
                <span style={{ color: colors.gray900 }}>2.4 MB</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Hash:</span>
                <span className="font-mono text-xs" style={{ color: colors.gray900 }}>3a5f...7b9d</span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="border rounded-xl p-4" style={{ borderColor: colors.border, backgroundColor: colors.white }}>
            <div className="flex items-center mb-3">
              <Activity size={16} className="mr-2" style={{ color: colors.warning }} />
              <div className="text-sm font-semibold" style={{ color: colors.gray900 }}>Performance</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Generation Speed:</span>
                <span className="text-green-600 font-medium">Instant</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Compliance Check:</span>
                <span className="text-green-600 font-medium">&lt; 50ms</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span style={{ color: colors.gray600 }}>Network Latency:</span>
                <span className="text-green-600 font-medium">12ms</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {showRealTimeData && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border rounded-xl p-4"
              style={{ borderColor: colors.success, backgroundColor: colors.successLight }}
            >
              <div className="flex items-center mb-3">
                <Download size={16} className="mr-2" style={{ color: colors.success }} />
                <div className="text-sm font-semibold" style={{ color: colors.success }}>Ready to Export</div>
              </div>
              <div className="space-y-2">
                {['PDF Certificate', 'JSON Data', 'Legal Report'].map((format, index) => (
                  <motion.button
                    key={format}
                    className="w-full p-2 rounded-lg border text-center hover:bg-white transition-all text-xs"
                    style={{ borderColor: colors.success + '40', color: colors.success }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {format}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

// End-to-End Signature Demo - combines autofill and signature into one seamless experience
export const EndToEndSignatureDemo = () => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timer, setTimer] = useState(0);
  const [autoplayComplete, setAutoplayComplete] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [signatureProgress, setSignatureProgress] = useState(0);
  const [showCompletion, setShowCompletion] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showCursor, setShowCursor] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [filledFields, setFilledFields] = useState(new Set()); // Track which fields have been filled
  const [userScrolledAway, setUserScrolledAway] = useState(false); // Track if user manually scrolled away
  const [lastAutoScrollTime, setLastAutoScrollTime] = useState(Date.now()); // Track when we last auto-scrolled
  const [lastAutoScrollTarget, setLastAutoScrollTarget] = useState(0); // Track the target position of last auto-scroll
  const [demoHasStarted, setDemoHasStarted] = useState(false); // Track if demo has actually started

  const demoRef = useRef(null);
  const signatureFieldRef = useRef(null);

  // Optimized step-by-step demo phases - self-managed timing
  const phases = useMemo(() => [
    { id: 'document-load', duration: 500, description: 'Document loads - AI analyzing fields' },
    { id: 'autofill-fields', duration: 0, description: 'AI auto-filling each field sequentially' }, // Self-managed
    { id: 'move-to-signature', duration: 500, description: 'Moving to signature field' },
    { id: 'apply-signature', duration: 800, description: 'Applying digital signature' },
    { id: 'legal-validation', duration: 600, description: 'Legal validation complete' },
    { id: 'show-results', duration: 1200, description: 'Speed comparison revealed' }
  ], []);

  // Enhanced user data for autofill - more realistic
  const userData = useMemo(() => ({
    company: "TechCorp Solutions Inc.",
    name: "Sarah Johnson",
    title: "Chief Technology Officer", 
    email: "sarah.johnson@techcorp.com",
    phone: "(415) 555-0123",
    address: "123 Innovation Drive, San Francisco, CA 94105",
    startDate: "January 15, 2024",
    rate: "$200.00 per hour",
    term: "12 months",
    completed: "47 contracts"
  }), []);

  // Enhanced fields to autofill in sequence following document visual order
  const contractFields = useMemo(() => [
    { id: "startDate", label: "Start Date", value: userData.startDate, icon: Calendar },
    { id: "company", label: "Company", value: userData.company, icon: FileCheck },
    { id: "name", label: "Service Provider Name", value: userData.name, icon: User },
    { id: "title", label: "Title", value: userData.title, icon: User },
    { id: "email", label: "Email", value: userData.email, icon: Mail },
    { id: "phone", label: "Phone", value: userData.phone, icon: Phone },
    { id: "address", label: "Address", value: userData.address, icon: MapPin },
    { id: "term", label: "Term", value: userData.term, icon: Clock },
    { id: "rate", label: "Rate", value: userData.rate, icon: DollarSign }
  ], [userData]);



  // Timer effect
  useEffect(() => {
    if (isPlaying && !autoplayComplete) {
      const interval = setInterval(() => {
        setTimer(prev => prev + 0.1);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isPlaying, autoplayComplete]);

  // Auto-play demo when component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isPlaying && !autoplayComplete) {
          setTimeout(() => {
            setIsPlaying(true);
          }, 800);
        }
      },
      { threshold: 0.4 }
    );

    const currentRef = demoRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isPlaying, autoplayComplete]);

  // Track user manual scrolling to prevent auto-scroll interference
  useEffect(() => {
    // Only start tracking scrolling after the demo has actually started
    if (!demoHasStarted) return;
    
    let lastScrollPosition = window.pageYOffset;
    let lastScrollTime = Date.now();
    let scrollVelocity = 0;
    
    const handleScroll = () => {
      const now = Date.now();
      const currentScrollPosition = window.pageYOffset;
      const scrollDelta = currentScrollPosition - lastScrollPosition;
      const timeDelta = now - lastScrollTime;
      
      // CRITICAL: Check if demo is still visible on screen
      if (demoRef.current) {
        const demoRect = demoRef.current.getBoundingClientRect();
        const demoTop = demoRect.top;
        const demoBottom = demoRect.bottom;
        const windowHeight = window.innerHeight;
        
        // If demo is completely out of view (user scrolled to different section)
        const isDemoOutOfView = demoBottom < -100 || demoTop > windowHeight + 100;
        
        // If user has scrolled far from the last auto-scroll target
        const isScrolledFarAway = Math.abs(currentScrollPosition - lastAutoScrollTarget) > windowHeight;
        
        if (isDemoOutOfView || isScrolledFarAway) {
          console.log('User scrolled away from demo section - stopping all auto-scroll:', {
            demoOutOfView: isDemoOutOfView,
            scrolledFarAway: isScrolledFarAway,
            demoTop: demoTop,
            demoBottom: demoBottom,
            currentScroll: currentScrollPosition,
            lastTarget: lastAutoScrollTarget
          });
          
          // Immediately stop all auto-scrolling
          setUserScrolledAway(true);
          return;
        }
      }
      
      // Calculate scroll velocity (pixels per second)
      if (timeDelta > 0) {
        scrollVelocity = Math.abs(scrollDelta) / (timeDelta / 1000);
      }
      
      // Track scroll magnitude
      if (Math.abs(scrollDelta) > 1) { // Only count meaningful scrolls
        lastScrollPosition = currentScrollPosition;
        lastScrollTime = now;
      }
      
      // ULTRA-SENSITIVE detection - ANY user scroll stops auto-scroll:
      // 1. Very quick time-based detection (300ms - just enough for auto-scroll to start)
      // 2. ANY velocity above minimal threshold (indicates user interaction)
      // 3. ANY scroll delta bigger than a few pixels
      const isTimeBasedUserScroll = now - lastAutoScrollTime > 300; // Very quick response
      const isAnyVelocityScroll = scrollVelocity > 100; // Any noticeable velocity
      const isAnyScroll = Math.abs(scrollDelta) > 5; // Even tiny scrolls count
      
      // If enough time has passed and there's ANY scroll, it's a user scroll
      const isUserScroll = isTimeBasedUserScroll && isAnyScroll;
      
      // Or if there's any immediate velocity, it's definitely user scroll
      const isImmediateUserScroll = isAnyVelocityScroll && Math.abs(scrollDelta) > 2;
      
      if ((isUserScroll || isImmediateUserScroll) && !userScrolledAway) {
        console.log('User scroll detected (ultra-sensitive) - stopping all auto-scroll:', {
          timeSinceAutoScroll: now - lastAutoScrollTime,
          currentPosition: currentScrollPosition,
          scrollDelta: scrollDelta,
          scrollVelocity: scrollVelocity,
          detectionReason: isImmediateUserScroll ? 'Immediate velocity' : 'Any scroll after delay'
        });
        
        // User touched the scroll - stop everything immediately
        setUserScrolledAway(true);
      } else if (!userScrolledAway) {
        // This is auto-scroll, let it continue
        if (Math.abs(scrollDelta) > 0) {
          console.log('Auto-scroll in progress:', {
            timeSinceAutoScroll: now - lastAutoScrollTime,
            scrollDelta: scrollDelta,
            scrollVelocity: scrollVelocity
          });
        }
      }
    };

    // ADDITIONAL: Detect wheel/touch/keyboard events for instant response
    const handleWheel = (e) => {
      if (!userScrolledAway && demoHasStarted) {
        console.log('Wheel event detected - user wants to scroll, stopping auto-scroll immediately');
        setUserScrolledAway(true);
      }
    };
    
    const handleTouchMove = (e) => {
      if (!userScrolledAway && demoHasStarted) {
        console.log('Touch move detected - user wants to scroll, stopping auto-scroll immediately');
        setUserScrolledAway(true);
      }
    };
    
    const handleKeyDown = (e) => {
      // Detect scroll-related keys: arrows, page up/down, home/end, spacebar
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (!userScrolledAway && demoHasStarted && scrollKeys.includes(e.key)) {
        console.log('Scroll key pressed - user wants to navigate, stopping auto-scroll immediately');
        setUserScrolledAway(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('keydown', handleKeyDown, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lastAutoScrollTime, lastAutoScrollTarget, demoHasStarted, userScrolledAway, demoRef]);

  // Phase progression - completely rewritten for proper sequencing
  useEffect(() => {
    if (!isPlaying) return;

    if (currentPhase < phases.length) {
      const phase = phases[currentPhase];
      
      const phaseTimer = setTimeout(() => {
        
        if (phase.id === 'document-load') {
          // Show cursor and prepare for autofill
          setShowCursor(true);
          setDemoHasStarted(true); // Mark that demo has actually started
          setCurrentPhase(prev => prev + 1);
          
        } else if (phase.id === 'autofill-fields') {
          // Sequential field filling - completely separate from phase timing
          let fieldIndex = 0;
          
          const fillNextField = () => {
            if (fieldIndex >= contractFields.length) {
              setCurrentPhase(prev => prev + 1);
              return;
            }
            
            const field = contractFields[fieldIndex];
            setCurrentFieldIndex(fieldIndex);
            
            // Step 1: Move cursor to field with retry logic
            const fieldElement = demoRef.current?.querySelector(`[data-field-id="${field.id}"]`);
            if (fieldElement && demoRef.current) {
              const fieldRect = fieldElement.getBoundingClientRect();
              const containerRect = demoRef.current.getBoundingClientRect();
              
              const newPosition = {
                x: fieldRect.left - containerRect.left + fieldRect.width / 2,
                y: fieldRect.top - containerRect.top + fieldRect.height / 2 - 25
              };
              setCursorPosition(newPosition);
              
              // Auto-scroll to keep the field in view with extra smooth timing (only if user hasn't scrolled away)
              setTimeout(() => {
                if (!userScrolledAway && demoRef.current) {
                  // SAFETY CHECK: Ensure demo is still in view before auto-scrolling
                  const demoRect = demoRef.current.getBoundingClientRect();
                  const isDemoVisible = demoRect.top < window.innerHeight && demoRect.bottom > 0;
                  
                  if (!isDemoVisible) {
                    console.log('Demo not visible - user navigated away, stopping auto-scroll');
                    setUserScrolledAway(true);
                    return;
                  }
                  
                  // Custom smooth scroll with gentle animation
                  const elementRect = fieldElement.getBoundingClientRect();
                  const absoluteElementTop = elementRect.top + window.pageYOffset;
                  const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
                  
                  console.log('Auto-scrolling to field:', field.id, {
                    target: middle,
                    userScrolledAway: userScrolledAway,
                    currentPosition: window.pageYOffset,
                    demoVisible: isDemoVisible
                  });
                  
                  // Set these BEFORE scrolling to prevent race conditions
                  const scrollTime = Date.now();
                  setLastAutoScrollTime(scrollTime);
                  setLastAutoScrollTarget(middle);
                  
                  window.scrollTo({
                    top: middle,
                    behavior: 'smooth'
                  });
                } else {
                  console.log('Skipping auto-scroll - user has scrolled away or demo not found');
                }
              }, 300);
              
              // Step 2: Show highlight after cursor arrives
              setTimeout(() => {
                setShowHighlight(true);
                
                // Step 3: Start filling field
                setTimeout(() => {
                  setFocusedField(field.id);
                  
                  // Step 4: Complete filling and mark as filled
                  setTimeout(() => {
                    setFilledFields(prev => new Set([...prev, field.id]));
                    setShowHighlight(false);
                    setFocusedField(null);
                    fieldIndex++;
                    
                    // Step 5: Move to next field after a pause
                    setTimeout(() => fillNextField(), 150);
                  }, 400); // Faster filling
                }, 100); // Faster timing
              }, 200); // Much faster cursor movement
            } else {
              // Field element not found, skip to next field
              fieldIndex++;
              setTimeout(() => fillNextField(), 100);
            }
          };
          
          // Start the sequential filling process immediately
          fillNextField();
          
        } else if (phase.id === 'move-to-signature') {
          // Clear any focused fields and move cursor to signature
          setFocusedField(null);
          setCurrentFieldIndex(-1);
          
          // Only proceed with signature auto-scroll if user hasn't taken control
          if (!userScrolledAway) {
            console.log('Moving to signature phase - user has not scrolled, proceeding with auto-scroll');
            
            // Move cursor to signature field with dramatic effect
            if (signatureFieldRef.current && demoRef.current) {
              const rect = signatureFieldRef.current.getBoundingClientRect();
              const containerRect = demoRef.current.getBoundingClientRect();
              setCursorPosition({
                x: rect.left - containerRect.left + rect.width / 2,
                y: rect.top - containerRect.top + rect.height / 2
              });
              
              // Auto-scroll to signature field only if user hasn't scrolled
              setTimeout(() => {
                if (!userScrolledAway && demoRef.current) {
                  // SAFETY CHECK: Ensure demo is still in view before auto-scrolling
                  const demoRect = demoRef.current.getBoundingClientRect();
                  const isDemoVisible = demoRect.top < window.innerHeight && demoRect.bottom > 0;
                  
                  if (!isDemoVisible) {
                    console.log('Demo not visible for signature - user navigated away, stopping auto-scroll');
                    setUserScrolledAway(true);
                    return;
                  }
                  
                  // Custom smooth scroll for signature field with gentle animation
                  const elementRect = signatureFieldRef.current.getBoundingClientRect();
                  const absoluteElementTop = elementRect.top + window.pageYOffset;
                  const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
                  
                  console.log('Auto-scrolling to signature field:', {
                    target: middle,
                    userScrolledAway: userScrolledAway,
                    currentPosition: window.pageYOffset,
                    demoVisible: isDemoVisible
                  });
                  
                  // Set these BEFORE scrolling to prevent race conditions
                  const scrollTime = Date.now();
                  setLastAutoScrollTime(scrollTime);
                  setLastAutoScrollTarget(middle);
                  
                  window.scrollTo({
                    top: middle,
                    behavior: 'smooth'
                  });
                }
              }, 400);
              
              // Show highlight around signature field
              setTimeout(() => {
                setShowHighlight(true);
                setCurrentPhase(prev => prev + 1);
              }, 400);
            } else {
              setCurrentPhase(prev => prev + 1);
            }
          } else {
            console.log('User has scrolled - skipping signature auto-scroll');
            // Still progress the demo, just without scrolling
            setCurrentPhase(prev => prev + 1);
          }
          
        } else if (phase.id === 'apply-signature') {
          // Apply the signature with animation
          setShowHighlight(false);
          setShowSignature(true);
          
          // Animate signature drawing
          const drawInterval = setInterval(() => {
            setSignatureProgress(prev => {
              if (prev >= 100) {
                clearInterval(drawInterval);
                return 100;
              }
              return prev + 10;
            });
          }, 60);
          
          // Hide cursor after signature starts
          setTimeout(() => {
            setShowCursor(false);
            setCurrentPhase(prev => prev + 1);
          }, 300);
          
        } else if (phase.id === 'legal-validation') {
          setShowCompletion(true);
          setCurrentPhase(prev => prev + 1);
          
        } else if (phase.id === 'show-results') {
          setTimeout(() => {
            setAutoplayComplete(true);
          }, 1500);
        }
        
      }, phase.duration);

      return () => clearTimeout(phaseTimer);
    }
  }, [isPlaying, currentPhase, phases, contractFields, demoRef, signatureFieldRef, userScrolledAway, demoHasStarted]);

  // Auto-reset
  useEffect(() => {
    if (autoplayComplete) {
      const timer = setTimeout(() => {
        setIsPlaying(false);
        setAutoplayComplete(false);
        setCurrentPhase(0);
        setTimer(0);
        setFocusedField(null);
        setShowSignature(false);
        setSignatureProgress(0);
        setShowCompletion(false);
        setShowCursor(false);
        setCursorPosition({ x: 0, y: 0 });
        setCurrentFieldIndex(0);
        setFilledFields(new Set());
        setShowHighlight(false);
        setUserScrolledAway(false); // Reset scroll tracking
        setLastAutoScrollTime(Date.now()); // Reset auto-scroll timer
        setLastAutoScrollTarget(0); // Reset auto-scroll target
        setDemoHasStarted(false); // Reset demo started flag
        
        // Restart after a brief pause
        setTimeout(() => {
          setIsPlaying(true);
        }, 2000);
      }, 6000);
      
      return () => clearTimeout(timer);
    }
  }, [autoplayComplete]);



  return (
    <div ref={demoRef} className="p-6 h-full" style={{ background: colors.white, fontFamily: fontStyles.fontFamily }}>
      {/* Header with Timer */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs" style={{ color: colors.gray500, ...fontStyles.body }}>
            Complete Signing Experience Demo
          </div>
          <div className="flex items-center mt-1">
            <div className="text-lg font-bold mr-3" style={{ color: colors.accent, ...fontStyles.heading }}>
              {timer.toFixed(1)}s
            </div>
            <div className="px-2 py-1 rounded-full text-xs" style={{ 
              backgroundColor: autoplayComplete ? colors.successLight : 
                             currentPhase >= 1 ? colors.accentLight : colors.gray100,
              color: autoplayComplete ? colors.success : 
                     currentPhase >= 1 ? colors.accent : colors.gray500,
              ...fontStyles.medium
            }}>
              {autoplayComplete ? 'Complete!' : 
               currentPhase === 0 ? 'Loading...' :
               currentPhase === 1 ? 'Auto-filling...' :
               currentPhase === 2 ? 'Moving to signature...' :
               currentPhase === 3 ? 'Signing...' : 
               currentPhase === 4 ? 'Validating...' : 'Comparing...'}
            </div>
            
            {/* User control indicator */}
            {userScrolledAway && !autoplayComplete && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, x: -10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                className="ml-3 px-3 py-1 rounded-full text-xs flex items-center"
                style={{ 
                  backgroundColor: colors.warningLight,
                  color: colors.warning,
                  border: `1px solid ${colors.warning}30`,
                  ...fontStyles.medium
                }}
              >
                <motion.div
                  className="w-2 h-2 rounded-full mr-2"
                  style={{ backgroundColor: colors.warning }}
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                Manual Control Active
              </motion.div>
            )}
            
            {/* Progress bar for autofill phase */}
            {currentPhase === 1 && (
              <div className="ml-3 flex items-center">
                <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${((currentFieldIndex + 1) / contractFields.length) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className="ml-2 text-xs" style={{ color: colors.gray600 }}>
                  {currentFieldIndex + 1}/{contractFields.length}
                </div>
              </div>
            )}
          </div>
        </div>
        {currentPhase >= 1 && currentPhase < 4 && (
          <motion.div 
            className="px-4 py-2 rounded-full text-xs font-medium border"
            style={{ 
              backgroundColor: currentPhase === 3 ? colors.warningLight : colors.accentLight, 
              color: currentPhase === 3 ? colors.warning : colors.accent,
              borderColor: (currentPhase === 3 ? colors.warning : colors.accent) + '30',
              ...fontStyles.medium
            }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            <div className="flex items-center">
              <motion.div
                className="w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: currentPhase === 3 ? colors.warning : colors.accent }}
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
              {currentPhase === 1 && (
                <>Auto-filling: {focusedField ? 
                  `${contractFields.find(f => f.id === focusedField)?.label} (${currentFieldIndex + 1}/${contractFields.length})` : 
                  `Processing... (${currentFieldIndex + 1}/${contractFields.length})`
                }</>
              )}
              {currentPhase === 2 && (
                <>Moving to Signature Field...</>
              )}
              {currentPhase === 3 && (
                <>Applying Digital Signature...</>
              )}
            </div>
          </motion.div>
        )}
        
        {currentPhase >= 3 && currentPhase < 5 && (
          <motion.div 
            className="px-4 py-2 rounded-full text-xs font-medium border"
            style={{ 
              backgroundColor: colors.warningLight, 
              color: colors.warning,
              borderColor: colors.warning + '30',
              ...fontStyles.medium
            }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 0.8, repeat: Infinity }}
          >
            <div className="flex items-center">
              <motion.div
                className="w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: colors.warning }}
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 0.6, repeat: Infinity }}
              />
              Digital Signature: {showSignature ? 'Applying signature...' : 'Ready to sign'}
            </div>
          </motion.div>
        )}
      </div>

      {/* Full-Width Document Demo */}
      <div className="relative">
        <div className="bg-white border rounded-xl p-8 relative overflow-hidden" style={{ 
          borderColor: colors.border, 
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
          minHeight: '500px'
        }}>
          
          {/* Enhanced AI Cursor with Intelligence Indicators */}
          <AnimatePresence>
            {showCursor && (
              <motion.div
                className="absolute z-50 pointer-events-none"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1,
                  x: cursorPosition.x - 16,
                  y: cursorPosition.y - 16
                }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                {/* AI Cursor with Enhanced Glow Effect */}
                <div className="relative">
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ 
                      background: `radial-gradient(circle, ${colors.accent}60 0%, ${colors.accent}20 50%, transparent 70%)`,
                      width: '40px',
                      height: '40px',
                      left: '-4px',
                      top: '-4px'
                    }}
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.6, 0.9, 0.6]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  
                  {/* Enhanced Main cursor - larger and more prominent */}
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="relative z-10">
                    <path d="M8 6L30 20L8 34V6Z" fill={colors.accent} stroke="white" strokeWidth="2" />
                    <circle cx="32" cy="8" r="6" fill={colors.warning} stroke="white" strokeWidth="2" />
                    <text x="32" y="12" textAnchor="middle" fontSize="9" fill="white" fontWeight="bold">AI</text>
                  </svg>

                  {/* Enhanced highlight circle around cursor - more prominent */}
                  <motion.div 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full border-4"
                    style={{ borderColor: colors.accent + 'CC', boxShadow: `0 0 20px ${colors.accent}60` }}
                    initial={{ width: 0, height: 0, opacity: 0 }}
                    animate={{ 
                      width: showHighlight ? 120 : 0, 
                      height: showHighlight ? 120 : 0,
                      opacity: showHighlight ? [0, 1, 0.6, 1, 0] : 0
                    }}
                    transition={{ duration: 2, repeat: showHighlight ? Infinity : 0 }}
                  />

                  {/* Additional pulsing ring for extra prominence */}
                  <motion.div 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
                    style={{ borderColor: colors.warning + '80' }}
                    initial={{ width: 0, height: 0, opacity: 0 }}
                    animate={{ 
                      width: showHighlight ? 160 : 0, 
                      height: showHighlight ? 160 : 0,
                      opacity: showHighlight ? [0, 0.5, 0] : 0
                    }}
                    transition={{ duration: 1.8, repeat: showHighlight ? Infinity : 0, delay: 0.2 }}
                  />
                </div>

                {/* Clean Professional Tooltip */}
                {focusedField && (
                  <motion.div
                    className="absolute top-8 left-4 bg-white border rounded-lg px-3 py-2 shadow-lg whitespace-nowrap"
                    style={{ 
                      borderColor: colors.border,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="flex items-center">
                      <div 
                        className="w-1.5 h-1.5 rounded-full mr-2"
                        style={{ backgroundColor: colors.accent }}
                      />
                      <div className="text-sm font-medium" style={{ color: colors.gray900 }}>
                        {contractFields.find(f => f.id === focusedField)?.label}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Document Header with Logo - Professional Legal Style */}
          <div className="flex justify-between items-center border-b pb-6 mb-8" style={{ borderColor: colors.border }}>
            <div className="flex items-center">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl mr-4 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                SP
              </div>
              <div>
                <div className="text-2xl font-bold mb-1" style={{ color: colors.gray900, ...fontStyles.heading }}>
                  Professional Services Agreement
                </div>
                <div className="text-sm flex items-center" style={{ color: colors.gray500, ...fontStyles.body }}>
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></div>
                  Document ID: PSA-2024-001547 • Ready for signature
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-sm font-medium">Draft</div>
            </div>
          </div>

          {/* Contract Content - Professional Legal Agreement */}
          <div className="space-y-6 mb-8">
            <div className="text-base leading-relaxed" style={{ color: colors.gray900, fontFamily: "'Times New Roman', serif", lineHeight: '1.6' }}>
              
              {/* Introduction */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-4" style={{ color: colors.gray900 }}>
                  PROFESSIONAL SERVICES AGREEMENT
                </h2>
              </div>

              <p className="mb-6">
                This Professional Services Agreement (the "Agreement") is made effective as of{' '}
                <motion.span 
                  className="relative min-w-[150px] border-b border-dashed border-gray-400 px-1"
                  data-field-id="startDate"
                  style={{
                    backgroundColor: focusedField === 'startDate' ? colors.accentLight : 'transparent',
                    borderColor: focusedField === 'startDate' ? colors.accent : '#9ca3af'
                  }}
                >
                  {filledFields.has('startDate') || focusedField === 'startDate' ? (
                    <motion.span 
                      style={{ color: colors.accent, fontWeight: 500 }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {userData.startDate}
                      {focusedField === 'startDate' && (
                        <motion.span
                          className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                          animate={{ opacity: [1, 0, 1] }}
                          transition={{ repeat: Infinity, duration: 0.8 }}
                        />
                      )}
                    </motion.span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>_____________</span>
                  )}
                  {focusedField === 'startDate' && (
                    <motion.div 
                      className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                      style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 0.95, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                    >
                      <Zap size={10} className="text-blue-500" />
                      <span>AI Auto-filled</span>
                    </motion.div>
                  )}
                </motion.span>
                , by and between the following parties:
              </p>
              
              {/* Parties Section */}
              <div className="mb-6">
                <p className="mb-3">
                  <strong>COMPANY:</strong>{' '}
                  <motion.span 
                    className="relative min-w-[180px] border-b border-dashed border-gray-400 px-1"
                    data-field-id="company"
                    style={{
                      backgroundColor: focusedField === 'company' ? colors.accentLight : 'transparent',
                      borderColor: focusedField === 'company' ? colors.accent : '#9ca3af'
                    }}
                  >
                    {filledFields.has('company') || focusedField === 'company' ? (
                      <motion.span 
                        style={{ color: colors.accent, fontWeight: 500 }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, type: "spring" }}
                      >
                        {userData.company}
                        {focusedField === 'company' && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </motion.span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>_____________________</span>
                    )}
                    {focusedField === 'company' && (
                      <motion.div 
                        className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                        style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 0.95, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                      >
                        <Zap size={10} className="text-blue-500" />
                        <span>AI Auto-filled</span>
                      </motion.div>
                    )}
                  </motion.span>
                  {' '}("Client")
                </p>
                
                <p className="mb-6">
                  <strong>SERVICE PROVIDER:</strong>{' '}
                  <motion.span 
                    className="relative min-w-[150px] border-b border-dashed border-gray-400 px-1"
                    data-field-id="name"
                    style={{
                      backgroundColor: focusedField === 'name' ? colors.accentLight : 'transparent',
                      borderColor: focusedField === 'name' ? colors.accent : '#9ca3af'
                    }}
                  >
                    {filledFields.has('name') || focusedField === 'name' ? (
                      <motion.span 
                        style={{ color: colors.accent, fontWeight: 500 }}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, type: "spring" }}
                      >
                        {userData.name}
                        {focusedField === 'name' && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </motion.span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>_________________</span>
                    )}
                    {focusedField === 'name' && (
                      <motion.div 
                        className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                        style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 0.95, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                      >
                        <Zap size={10} className="text-blue-500" />
                        <span>AI Auto-filled</span>
                      </motion.div>
                    )}
                  </motion.span>
                  {', '}
                  <motion.span 
                    className="relative min-w-[120px] border-b border-dashed border-gray-400 px-1"
                    data-field-id="title"
                    style={{
                      backgroundColor: focusedField === 'title' ? colors.accentLight : 'transparent',
                      borderColor: focusedField === 'title' ? colors.accent : '#9ca3af'
                    }}
                  >
                    {filledFields.has('title') || focusedField === 'title' ? (
                      <motion.span 
                        style={{ color: colors.accent, fontWeight: 500 }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, type: "spring" }}
                      >
                        {userData.title}
                        {focusedField === 'title' && (
                          <motion.span
                            className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          />
                        )}
                      </motion.span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>______________</span>
                    )}
                    {focusedField === 'title' && (
                      <motion.div 
                        className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                        style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 0.95, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                      >
                        <Zap size={10} className="text-blue-500" />
                        <span>AI Auto-filled</span>
                      </motion.div>
                    )}
                  </motion.span>
                  {' '}("Provider")
                </p>
              </div>

              {/* Contact Information Section */}
              <div className="mb-8 bg-gray-50 p-6 rounded-lg border border-gray-100">
                <h3 className="font-bold text-lg mb-4" style={{ color: colors.gray900 }}>CONTACT INFORMATION:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-base">
                  <div>
                    <p className="mb-3">
                      <strong>Email:</strong>{' '}
                      <motion.span 
                        className="relative border-b border-dashed border-gray-400 px-1"
                        data-field-id="email"
                        style={{
                          backgroundColor: focusedField === 'email' ? colors.accentLight : 'transparent',
                          borderColor: focusedField === 'email' ? colors.accent : '#9ca3af'
                        }}
                      >
                        {filledFields.has('email') || focusedField === 'email' ? (
                          <motion.span 
                            style={{ color: colors.accent, fontWeight: 500 }}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, type: "spring" }}
                          >
                            {userData.email}
                            {focusedField === 'email' && (
                              <motion.span
                                className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                                animate={{ opacity: [1, 0, 1] }}
                                transition={{ repeat: Infinity, duration: 0.8 }}
                              />
                            )}
                          </motion.span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>____________________</span>
                        )}
                        {focusedField === 'email' && (
                          <motion.div 
                            className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                            style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 0.95, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.4 }}
                          >
                            <Zap size={10} className="text-blue-500" />
                            <span>AI Auto-filled</span>
                          </motion.div>
                        )}
                      </motion.span>
                    </p>
                    <p className="mb-3">
                      <strong>Phone:</strong>{' '}
                      <motion.span 
                        className="relative border-b border-dashed border-gray-400 px-1"
                        data-field-id="phone"
                        style={{
                          backgroundColor: focusedField === 'phone' ? colors.accentLight : 'transparent',
                          borderColor: focusedField === 'phone' ? colors.accent : '#9ca3af'
                        }}
                      >
                        {filledFields.has('phone') || focusedField === 'phone' ? (
                          <motion.span 
                            style={{ color: colors.accent, fontWeight: 500 }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                          >
                            {userData.phone}
                            {focusedField === 'phone' && (
                              <motion.span
                                className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                                animate={{ opacity: [1, 0, 1] }}
                                transition={{ repeat: Infinity, duration: 0.8 }}
                              />
                            )}
                          </motion.span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>___________________</span>
                        )}
                        {focusedField === 'phone' && (
                          <motion.div 
                            className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                            style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 0.95, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.4 }}
                          >
                            <Zap size={10} className="text-blue-500" />
                            <span>AI Auto-filled</span>
                          </motion.div>
                        )}
                      </motion.span>
                    </p>
                  </div>
                  <div>
                    <p className="mb-3">
                      <strong>Address:</strong>{' '}
                      <motion.span 
                        className="relative border-b border-dashed border-gray-400 px-1"
                        data-field-id="address"
                        style={{
                          backgroundColor: focusedField === 'address' ? colors.accentLight : 'transparent',
                          borderColor: focusedField === 'address' ? colors.accent : '#9ca3af'
                        }}
                      >
                        {filledFields.has('address') || focusedField === 'address' ? (
                          <motion.span 
                            style={{ color: colors.accent, fontWeight: 500 }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                          >
                            {userData.address}
                            {focusedField === 'address' && (
                              <motion.span
                                className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                                animate={{ opacity: [1, 0, 1] }}
                                transition={{ repeat: Infinity, duration: 0.8 }}
                              />
                            )}
                          </motion.span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>___________________</span>
                        )}
                        {focusedField === 'address' && (
                          <motion.div 
                            className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                            style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 0.95, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.4 }}
                          >
                            <Zap size={10} className="text-blue-500" />
                            <span>AI Auto-filled</span>
                          </motion.div>
                        )}
                      </motion.span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Agreement Terms */}
              <h3 className="font-bold text-lg border-b pb-2 mb-4" style={{ borderColor: colors.border, color: colors.gray900 }}>
                1. ENGAGEMENT TERMS
              </h3>
              <p className="mb-4">
                1.1 The Client hereby engages the Provider, and the Provider hereby accepts the engagement, to provide professional services to the Client on the terms and conditions set forth in this Agreement.
              </p>
              <p className="mb-6">
                1.2 The engagement shall begin on the date first written above and continue for{' '}
                <motion.span 
                  className="relative border-b border-dashed border-gray-400 px-1"
                  data-field-id="term"
                  style={{
                    backgroundColor: focusedField === 'term' ? colors.accentLight : 'transparent',
                    borderColor: focusedField === 'term' ? colors.accent : '#9ca3af'
                  }}
                >
                  {filledFields.has('term') || focusedField === 'term' ? (
                    <motion.span 
                      style={{ color: colors.accent, fontWeight: 500 }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                    >
                      {userData.term}
                      {focusedField === 'term' && (
                        <motion.span
                          className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                          animate={{ opacity: [1, 0, 1] }}
                          transition={{ repeat: Infinity, duration: 0.8 }}
                        />
                      )}
                    </motion.span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>__________</span>
                  )}
                  {focusedField === 'term' && (
                    <motion.div 
                      className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                      style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 0.95, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                    >
                      <Zap size={10} className="text-blue-500" />
                      <span>AI Auto-filled</span>
                    </motion.div>
                  )}
                </motion.span>
                , unless terminated earlier in accordance with the provisions of this Agreement.
              </p>

              <h3 className="font-bold text-lg border-b pb-2 mb-4" style={{ borderColor: colors.border, color: colors.gray900 }}>
                2. COMPENSATION
              </h3>
              <p className="mb-4">
                2.1 In consideration for the services provided, the Client agrees to pay the Provider{' '}
                <motion.span 
                  className="relative border-b border-dashed border-gray-400 px-1"
                  data-field-id="rate"
                  style={{
                    backgroundColor: focusedField === 'rate' ? colors.accentLight : 'transparent',
                    borderColor: focusedField === 'rate' ? colors.accent : '#9ca3af'
                  }}
                >
                  {filledFields.has('rate') || focusedField === 'rate' ? (
                    <motion.span 
                      style={{ color: colors.accent, fontWeight: 500 }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                    >
                      {userData.rate}
                      {focusedField === 'rate' && (
                        <motion.span
                          className="inline-block h-4 w-[1.5px] bg-blue-500 ml-1"
                          animate={{ opacity: [1, 0, 1] }}
                          transition={{ repeat: Infinity, duration: 0.8 }}
                        />
                      )}
                    </motion.span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>_______________</span>
                  )}
                  {focusedField === 'rate' && (
                    <motion.div 
                      className="absolute -bottom-6 left-0 text-xs px-2 py-1 rounded-full text-blue-600 font-medium flex items-center gap-1"
                      style={{ backgroundColor: 'rgba(239, 246, 255, 0.9)', border: '1px solid rgba(191, 219, 254, 0.5)' }}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 0.95, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                    >
                      <Zap size={10} className="text-blue-500" />
                      <span>AI Auto-filled</span>
                    </motion.div>
                  )}
                </motion.span>
                .
              </p>
              <p className="mb-6">
                2.2 Payment shall be made within thirty (30) days of receiving Provider's invoice.
              </p>

              <h3 className="font-bold text-lg border-b pb-2 mb-4" style={{ borderColor: colors.border, color: colors.gray900 }}>
                3. CONFIDENTIALITY
              </h3>
              <p className="mb-6">
                3.1 During the term of this Agreement and thereafter, the Provider shall keep confidential and shall not disclose any confidential or proprietary information relating to the Client's business or operations.
              </p>

              <h3 className="font-bold text-lg border-b pb-2 mb-4" style={{ borderColor: colors.border, color: colors.gray900 }}>
                4. GOVERNING LAW
              </h3>
              <p className="mb-8">
                4.1 This Agreement shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of laws principles.
              </p>

              <p className="mb-6 text-center font-semibold">
                <strong>IN WITNESS WHEREOF,</strong> the parties have executed this Agreement as of the date first written above.
              </p>
            </div>
          </div>

          {/* Signature Section - Integrated with Contract */}
          <div className="border-t pt-6" style={{ borderColor: colors.border }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" style={{ fontFamily: "'Times New Roman', serif" }}>
              <div>
                <p className="mb-4 text-base"><strong>CLIENT SIGNATURE:</strong></p>
                <motion.div
                  ref={signatureFieldRef}
                  className="relative p-4 h-24 transition-all duration-500 overflow-hidden mb-4 rounded-lg"
                  style={{
                    border: `2px solid ${showSignature ? colors.success : 
                               currentPhase >= 2 ? colors.accent : colors.gray300}`,
                    backgroundColor: showSignature ? colors.successLight : 
                                   currentPhase >= 2 ? colors.accentLight : colors.gray50
                  }}
                  animate={{
                    scale: currentPhase === 2 ? [1, 1.05, 1] : currentPhase === 3 ? [1, 1.02, 1] : 1,
                    boxShadow: currentPhase >= 2 ? '0 0 25px rgba(30, 64, 175, 0.4)' : 'none',
                    borderColor: showSignature ? colors.success : 
                                currentPhase >= 2 ? colors.accent : colors.gray300
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {!showSignature ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <motion.div
                          animate={{ 
                            scale: currentPhase >= 2 ? [1, 1.1, 1] : 1,
                            rotate: currentPhase >= 2 ? [0, 2, -2, 0] : 0
                          }}
                          transition={{ duration: 0.5, repeat: currentPhase >= 2 ? Infinity : 0, repeatDelay: 2 }}
                        >
                          <Zap size={28} className="mx-auto mb-2" style={{ color: colors.accent }} />
                        </motion.div>
                        <div className="text-sm font-semibold" style={{ color: colors.accent, ...fontStyles.heading }}>
                          {currentPhase >= 3 ? '✨ Applying Signature...' : 
                           currentPhase >= 2 ? '🎯 Ready for Signature' : 
                           'Digital Signature Area'}
                        </div>
                        <div className="text-xs mt-1" style={{ color: colors.gray600 }}>
                          {currentPhase >= 3 ? 'AI generating signature...' :
                           currentPhase >= 2 ? 'All fields completed • Ready to sign' : 
                           'Signature will appear after auto-fill'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative h-full flex items-center justify-center">
                      {/* Signature with realistic handwriting effect */}
                      <motion.div
                        className="relative"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.div
                          className="text-4xl mx-auto text-center"
                          style={{ 
                            fontFamily: "'Yellowtail', 'Dancing Script', 'Great Vibes', cursive",
                            transform: 'rotate(-1deg)',
                            letterSpacing: '0.05em',
                            color: colors.accent,
                            fontWeight: 400,
                            textShadow: '0 1px 2px rgba(30, 64, 175, 0.1)',
                            lineHeight: 1.2
                          }}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: signatureProgress / 100 }}
                          transition={{ duration: 1.2, ease: "easeInOut" }}
                        >
                          Sarah Johnson
                        </motion.div>
                        
                        {/* Signature completion sparkle effect */}
                        {signatureProgress >= 100 && (
                          <motion.div
                            className="absolute -top-2 -right-2"
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                          >
                            <div className="relative">
                              <CheckCircle size={24} style={{ color: colors.success }} />
                              <motion.div
                                className="absolute inset-0"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                              >
                                <Sparkles size={24} style={{ color: colors.warning, opacity: 0.7 }} />
                              </motion.div>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    </div>
                  )}
                </motion.div>
                
                {/* Name and date fields */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Signatory:</span>
                    <span className="text-sm font-medium" style={{ color: colors.gray900 }}>
                      {currentPhase > 1 ? userData.name : 'Will auto-populate'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Date:</span>
                    <span className="text-sm font-medium" style={{ color: colors.gray900 }}>
                      {currentPhase > 1 ? userData.date : 'Will auto-populate'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <p className="mb-4 text-base"><strong>PROVIDER SIGNATURE:</strong></p>
                <div className="border-b-2 border-gray-300 p-4 h-24 mb-4 flex items-center">
                  <div className="text-2xl font-script text-gray-700" style={{ fontFamily: "'Dancing Script', cursive" }}>
                    Michael Chen
                  </div>
                </div>
                <div className="text-sm text-gray-600">Michael Chen</div>
                <div className="text-xs text-gray-500 mt-1">Print Name</div>
                <div className="text-xs text-gray-500 mt-2">Date: December 15, 2024</div>
              </div>
            </div>

            {/* Legal validation badges */}
            {showCompletion && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 flex items-center justify-center gap-4 text-sm"
              >
                {[
                  { icon: Shield, text: 'UETA Compliant' },
                  { icon: Check, text: 'Legally Binding' },
                  { icon: Clock, text: 'Instant Audit Trail' }
                ].map((badge, index) => (
                  <motion.div 
                    key={badge.text}
                    className="flex items-center px-4 py-2 rounded-full shadow-sm"
                    style={{ backgroundColor: colors.successLight, color: colors.success }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.2, type: "spring" }}
                  >
                    <badge.icon size={16} className="mr-2" />
                    {badge.text}
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </div>


      </div>
    </div>
  );
};

// Main SignatureDemo component - combines all demos
export const SignatureDemo = ({ selectedFeature = 'complete-experience', onFeatureChange }) => {
  const featureComponents = {
    'complete-experience': <EndToEndSignatureDemo />,
    'compliance-demo': <ComplianceDemo />,
    'audit-demo': <AuditTrailDemo />
  };

  return (
    <div className="h-full">
      {featureComponents[selectedFeature] || featureComponents['complete-experience']}
    </div>
  );
};

